import { useRef, useState } from 'react';
import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import logger from '@eneco/common/log';
import { toURL } from '@eneco/common/path';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRouter } from '@eneco/next';
import { Button, Heading, NotificationBox, Stack, Text } from '@eneco/sparky';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import BudgetManagerContactInputs from './contact-form/BudgetManagerContactInputs';
import ContactInputs from './contact-form/ContactInputs';
import { generateErrorType } from './contact-form/error';
import InquiryInputs from './contact-form/InquiryInputs';
import { optionsOf } from './contact-form/options';
import { getSchema } from './contact-form/schema';
import type { FormFields } from './contact-form/types';
import { usePostInquiry } from './contact-form/usePostInquiry';
import type { ContactFormContent, ContactFormFields, ContactNotification } from './contact-form-content.types';
import type { FormErrorType } from './generic-form/types';

const FIELD_ROLES: (keyof ContactFormFields)[] = [
  'budgetManagerToggleFormField',
  'salutationFormField',
  'firstNameFormField',
  'surnamePrepositionFormField',
  'surnameFormField',
  'phoneNumberFormField',
  'emailAddressFormField',
  'customerIdFormField',
  'inquiryTopicFormField',
  'inquiryTypeFormField',
  'inquiryContentFormField',
  'businessNameFormField',
  'postalCodeFormField',
  'houseNumberFormField',
  'houseNumberSuffixFormField',
  'streetNameFormField',
  'cityFormField',
];

// On Sitecore `formFields` is also the template section itself (an object), next to the flattened
// descriptors; only Sanity's role-keyed lists are arrays.
const findByRole = <T extends { role?: string }>(entries: T[] | object | undefined, role: string): T | undefined =>
  Array.isArray(entries) ? (entries as T[]).find(entry => entry.role === role) : undefined;

/** Each descriptor from Sanity's role-keyed lists, or from the flat name the Sitecore adapter gives it. */
const readFormFields = (content: ContactFormContent): ContactFormFields =>
  Object.fromEntries(
    FIELD_ROLES.map(role => [
      role,
      findByRole(content.formFields, role) ??
        findByRole(content.radioGroups, role) ??
        findByRole(content.selectFields, role) ??
        content[role] ??
        {},
    ]),
  ) as unknown as ContactFormFields;

/**
 * `ContactForm` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/forms/src/ContactForm/ContactForm.tsx` 1:1: the inquiry topic (with
 * a message for some topics), the question and a file upload, the contact details and, where the form
 * allows it, a budget manager's details; posted as a customer inquiry, then redirecting to the submit
 * link. A failure shows the file or generic error notification. Drops the legacy `wrap()`/Experience
 * Editor `editable` checks — this lane never runs inside the Experience Editor.
 *
 * `budgetManagerAvailable` is a rendering parameter on Sitecore (`'1'`) and a boolean field on Sanity,
 * which `useSetting` hands over as `'true'`.
 */
const ContactForm: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ContactFormContent>();
  const formFields = readFormFields(content);
  const budgetManagerAvailable = useSetting('budgetManagerAvailable');
  const canHaveBudgetManager = budgetManagerAvailable === '1' || budgetManagerAvailable === 'true';
  const budgetManagerContent = findByRole(content.richText, 'budgetManagerContent')?.content ?? content.budgetManagerContent;

  const postInquiry = usePostInquiry();
  const { pushHref } = useRouter();

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [submittedFiles, setSubmittedFiles] = useState<File[]>([]);
  const [hasFileError, setHasFileError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorType, setErrorType] = useState<FormErrorType | undefined>();

  const formSchema = getSchema(formFields, content.hideInquiryTypeFormFieldCheckbox, canHaveBudgetManager);
  const methods = useForm<FormFields>({
    resolver: typedYupResolver<FormFields>(formSchema),
  });
  const {
    control,
    formState: { errors },
    register,
    watch,
  } = methods;
  const authorIsBudgetManager = !!watch('authorIsBudgetManager');
  const selectedTopic = watch('inquiryTopic');

  const submitForm: SubmitHandler<FormFields> = async input => {
    setIsSubmitting(true);
    // We keep the topic value for internal form logic, but we want to send the label to the backend
    const subject = optionsOf(formFields.inquiryTopicFormField).find(topic => topic.value === selectedTopic)?.label || '';

    if (!hasFileError) {
      try {
        await postInquiry(input, submittedFiles, subject);
        setErrorType(undefined);
        pushHref(toURL(content.submitButtonLink?.href ?? ''));
      } catch (e) {
        logger.error('pqRD2C', 'Failed to submit form', { error: e });
        setErrorType(generateErrorType(e));
      }
      setIsSubmitting(false);
    }
  };

  const sharedProps = { formFields, register, errors };
  const notification: ContactNotification | undefined = errorType
    ? (findByRole(content.notifications, `${errorType}ErrorNotification`) ?? content[`${errorType}ErrorNotification`])
    : undefined;

  return (
    <FormProvider {...methods}>
      <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef}>
        <Stack gap="10">
          <Stack gap="2">
            <Heading size="M" as="h2">
              {content.title}
            </Heading>
            <Text size="BodyL">
              <RichText html={adapter.toRichText(content.description)} />
            </Text>
          </Stack>
          <Stack gap="6">
            <ContactInputs
              control={control}
              contactDetailsTitle={content.contactDetailsTitle}
              budgetManagerContent={budgetManagerContent}
              authorIsBudgetManager={authorIsBudgetManager}
              canHaveBudgetManager={canHaveBudgetManager}
              {...sharedProps}
            />
            <InquiryInputs
              inquiryTitle={content.inquiryTitle}
              hideInquiryTypeFormFieldCheckbox={content.hideInquiryTypeFormFieldCheckbox}
              actions={content.actions ?? []}
              files={submittedFiles}
              setFiles={setSubmittedFiles}
              hasError={hasFileError}
              setHasError={setHasFileError}
              selectedTopic={selectedTopic}
              {...sharedProps}
            />
            {authorIsBudgetManager && (
              <BudgetManagerContactInputs
                control={control}
                title={content.budgetManagerContactDetailsTitle}
                {...sharedProps}
              />
            )}

            <Text size="BodyS">
              <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
            </Text>

            {errorType ? (
              <NotificationBox
                isAlert
                text={<RichText html={adapter.toRichText(notification?.content)} />}
                title={notification?.title}
                variant={notification?.variant}
              />
            ) : null}
            <Stack.Item>
              <Button type="submit" isLoading={isSubmitting}>
                {content.submitButtonLink?.text}
              </Button>
            </Stack.Item>
          </Stack>
        </Stack>
      </RecaptchaForm>
    </FormProvider>
  );
};

export default ContactForm;
